import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Landmark, AlertTriangle, Sparkles, Check, Loader2, BookmarkPlus } from 'lucide-react';
import { Switch } from '../ui/switch';
import { fmtGBP } from './LineItemsTable';
import { CIS_RATES, VAT_TREATMENTS, CIS_CATEGORIES, cisWorkings, rateLabel } from './cisRules';

const seg = (active, tone = 'amber') => `px-2.5 h-7 text-xs font-medium transition-colors ${active ? (tone === 'amber' ? 'bg-[#F5A623] text-[#1E2530]' : 'bg-emerald-500 text-white') : 'bg-[#1E2530] text-gray-300 hover:bg-white/5'}`;

function Segmented({ options, value, onChange, disabled, testid, tone }) {
  return (
    <div className="inline-flex rounded-md overflow-hidden border border-white/15" data-testid={testid}>
      {options.map(o => (
        <button key={String(o.value)} type="button" disabled={disabled} onClick={() => onChange(o.value)} title={o.hint || ''}
          className={seg(value === o.value, tone)} data-testid={`${testid}-${String(o.value).replace('_', '-')}`}>{o.label}</button>
      ))}
    </div>
  );
}

function WorkingsTile({ w, rate, vatTreatment, compact = false }) {
  const rows = [
    ['Labour', w.labour, 'labour'],
    ['Materials', w.material, 'materials'],
    ...(w.fuel > 0 ? [['Fuel', w.fuel, 'fuel']] : []),
    [`CIS deduction · ${Number(rate) === 0 ? 'gross status' : `${Number(rate)}% of ${fmtGBP(w.base)}`}`, -w.deduction, 'deduction'],
    ['Total paid after tax', w.payable, 'payable'],
  ];
  return (
    <div className={`rounded-md border border-[#F5A623]/30 bg-[#F5A623]/[0.06] ${compact ? 'p-2' : 'p-2.5'}`} data-testid="cis-workings-tile">
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <span className="text-xs text-[#F5A623] font-semibold inline-flex items-center gap-1.5"><Landmark className="w-3.5 h-3.5" /> CIS workings</span>
        <span className="text-[10px] uppercase tracking-wider text-gray-400">{VAT_TREATMENTS.find(v => v.value === vatTreatment)?.label || 'No VAT'}</span>
      </div>
      <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-sm">
        {rows.map(([label, pence, key]) => (
          <div key={key} className={`contents ${key === 'payable' ? 'font-semibold' : ''}`}>
            <span className={`${key === 'payable' ? 'text-white' : key === 'deduction' ? 'text-[#F5A623]' : 'text-gray-300'} text-xs self-center`}>{label}</span>
            <span className={`text-right tabular-nums ${key === 'payable' ? 'text-white text-base' : key === 'deduction' ? 'text-[#F5A623]' : 'text-gray-200'}`} data-testid={`cis-workings-${key}`}>
              {key === 'deduction' ? `− ${fmtGBP(Math.abs(pence))}` : fmtGBP(pence)}
            </span>
          </div>
        ))}
      </div>
      <div className="text-[10px] text-gray-500 mt-1.5">Deduction is withheld on labour only and paid to HMRC — materials and fuel are exempt; project costs carry the full line values.</div>
    </div>
  );
}

/**
 * CIS block for the Process dialog: mark the invoice as CIS, pick the VAT treatment
 * (no VAT / reverse charge 0%) and the deduction status (20 / 30 / gross), see the
 * workings. Known CIS suppliers default the invoice to CIS; the admin can override.
 */
export function CisPanel({ api, item, form, setForm, lines, setLines, readOnly, aiTotals }) {
  const cis = form.cis;
  const supplierName = form.supplier_name;
  const [known, setKnown] = useState(undefined); // undefined = not looked up yet
  const [saving, setSaving] = useState(false);
  const lastLookup = useRef('');
  // CIS invoices carry 0% VAT (not registered / reverse charge) — every line follows, so nobody zeroes them by hand.
  const zeroLineRates = () => setLines?.(ls => ls.map(l => (l.vat_rate === '0' || l.vat_rate === 0) ? l : { ...l, vat_rate: '0', vat_source: 'cis' }));
  const restoreLineRates = () => setLines?.(ls => ls.map(l => l.vat_source === 'cis' ? { ...l, vat_rate: null, vat_source: null } : l));

  useEffect(() => {
    const name = (supplierName || '').trim();
    if (!api || name.length < 2) { setKnown(null); return undefined; }
    if (lastLookup.current === name) return undefined;
    const t = setTimeout(async () => {
      try {
        const r = await api.get('/cost-inbox/cis-suppliers/lookup', { params: { name } });
        lastLookup.current = name;
        setKnown(r.data.supplier || null);
      } catch { setKnown(null); }
    }, 450);
    return () => clearTimeout(t);
  }, [api, supplierName]);

  const enable = (on, src = 'user') => {
    setForm(f => {
      if (on) {
        const k = known && known.cis ? known : null;
        return { ...f, cis: { ...f.cis, enabled: true, user_set: true, source: src, rate: k ? k.rate : f.cis.rate, vat_treatment: k ? k.vat_treatment : f.cis.vat_treatment },
          vat: '0.00', vat_status: 'ok', vat_note: '' };
      }
      const aiVat = aiTotals?.vat_pounds;
      return { ...f, cis: { ...f.cis, enabled: false, user_set: true, source: 'user' },
        vat: aiVat != null ? aiVat.toFixed(2) : '', vat_status: aiVat != null ? 'check' : 'unknown', vat_note: aiVat != null ? 'CIS switched off — check the VAT against the invoice' : '' };
    });
    if (on) zeroLineRates(); else restoreLineRates();
  };
  const setCis = (patch) => {
    setForm(f => ({ ...f, cis: { ...f.cis, ...patch, user_set: true, source: 'user' } }));
    if (patch.vat_treatment) zeroLineRates();
  };

  const w = cisWorkings(lines, cis.rate);
  const offCategory = lines.filter(l => l.category && !CIS_CATEGORIES.includes(l.category)).length;
  const ex = item?.extraction || {};
  const aiHint = !readOnly && !cis.enabled && (ex.cis_mentioned || ex.reverse_charge);
  const knownCis = known && known.cis;
  const differs = cis.enabled && (!knownCis || Number(known.rate) !== Number(cis.rate) || (known.vat_treatment || 'no_vat') !== cis.vat_treatment);

  const saveDefault = async () => {
    setSaving(true);
    try {
      const r = await api.post('/cost-inbox/cis-suppliers', { supplier_name: supplierName, rate: cis.rate, vat_treatment: cis.vat_treatment, cis: true });
      setKnown(r.data.supplier);
      lastLookup.current = (supplierName || '').trim();
      toast.success(`${supplierName} saved as a CIS sub-contractor · ${rateLabel(cis.rate)}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); }
    finally { setSaving(false); }
  };

  if (readOnly) {
    if (!item?.cis?.enabled) return null;
    const sw = item.cis_workings;
    const posted = sw ? { labour: sw.labour_pence, material: sw.material_pence, fuel: sw.fuel_pence, base: sw.base_pence, deduction: sw.deduction_pence, payable: sw.payable_pence } : w;
    return (
      <div className="rounded-lg border border-white/10 bg-white/[0.03] p-2.5 space-y-2" data-testid="inbox-cis-block">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <span className="text-xs text-gray-300 font-medium inline-flex items-center gap-1.5"><Landmark className="w-3.5 h-3.5 text-[#F5A623]" /> CIS invoice</span>
          <span className="text-xs text-white font-semibold px-2 py-0.5 rounded bg-white/5 border border-white/10" data-testid="inbox-cis-readonly-rate">{rateLabel(item.cis.rate)}</span>
        </div>
        <WorkingsTile w={posted} rate={item.cis.rate} vatTreatment={item.cis.vat_treatment} compact />
      </div>
    );
  }

  return (
    <div className={`rounded-lg border p-2.5 space-y-2 ${cis.enabled ? 'border-[#F5A623]/40 bg-[#F5A623]/[0.04]' : 'border-white/10 bg-white/[0.03]'}`} data-testid="inbox-cis-block">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <label className="flex items-center gap-2.5 cursor-pointer select-none">
          <Switch checked={cis.enabled} onCheckedChange={(v) => enable(v)} className="data-[state=checked]:bg-[#F5A623]" data-testid="inbox-cis-toggle" />
          <span>
            <span className="block text-xs text-gray-200 font-medium inline-flex items-center gap-1.5"><Landmark className="w-3.5 h-3.5 text-[#F5A623]" /> CIS invoice <span className="text-gray-500 font-normal">· sub-contractor</span></span>
          </span>
        </label>
        {knownCis && (
          <span className="text-[11px] text-emerald-300 inline-flex items-center gap-1 bg-emerald-500/10 border border-emerald-500/25 rounded px-2 py-0.5" data-testid="inbox-cis-known">
            <Check className="w-3 h-3" /> Known CIS sub-contractor · {rateLabel(known.rate)}
            <span className="text-emerald-300/70">· {known.source === 'learned' ? `learned from ${known.invoices_seen} invoice${known.invoices_seen === 1 ? '' : 's'}` : 'set by admin'}</span>
          </span>
        )}
      </div>

      {!cis.enabled && knownCis && !cis.user_set && (
        <div className="text-[11px] text-gray-400">This supplier is on the CIS list — <button type="button" onClick={() => enable(true, 'supplier')} className="text-[#F5A623] hover:underline" data-testid="inbox-cis-apply-known">mark this invoice as CIS</button>.</div>
      )}
      {aiHint && (
        <div className="text-[11px] text-amber-200 flex items-start gap-1.5 bg-amber-500/10 border border-amber-500/25 rounded px-2 py-1" data-testid="inbox-cis-ai-hint">
          <Sparkles className="w-3 h-3 mt-0.5 shrink-0" />
          <span>The reader saw {ex.cis_mentioned && ex.reverse_charge ? 'CIS and reverse-charge' : ex.cis_mentioned ? 'CIS' : 'reverse-charge VAT'} wording on this invoice.
            <button type="button" onClick={() => enable(true)} className="text-[#F5A623] hover:underline ml-1.5" data-testid="inbox-cis-ai-apply">Mark as CIS</button>
          </span>
        </div>
      )}

      {cis.enabled && (
        <>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="text-[11px] text-gray-400 mb-1">VAT on this invoice</div>
              <Segmented options={VAT_TREATMENTS} value={cis.vat_treatment} onChange={(v) => setCis({ vat_treatment: v })} testid="inbox-cis-vat" />
              <div className="text-[10px] text-gray-500 mt-1" data-testid="inbox-cis-lines-zeroed">Line VAT rates set to 0% automatically</div>
            </div>
            <div>
              <div className="text-[11px] text-gray-400 mb-1">CIS status · deduction</div>
              <Segmented options={CIS_RATES} value={Number(cis.rate)} onChange={(v) => setCis({ rate: v })} testid="inbox-cis-rate" />
            </div>
          </div>
          {differs && (supplierName || '').trim().length >= 2 && (
            <button type="button" onClick={saveDefault} disabled={saving} className="inline-flex items-center gap-1.5 text-[11px] text-sky-200 bg-sky-500/10 hover:bg-sky-500/20 border border-sky-500/30 rounded px-2 py-1 disabled:opacity-50" data-testid="inbox-cis-save-default">
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <BookmarkPlus className="w-3 h-3" />}
              {knownCis ? `Update ${supplierName}'s default to ${rateLabel(cis.rate)}` : `Save ${supplierName} as a CIS sub-contractor · ${rateLabel(cis.rate)}`}
            </button>
          )}
          {offCategory > 0 && (
            <div className="text-[11px] text-amber-200 flex items-start gap-1.5" data-testid="inbox-cis-category-warning">
              <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" /> CIS invoices use Labour, Materials or Fuel lines — change {offCategory} line{offCategory === 1 ? '' : 's'} before posting.
            </div>
          )}
          <WorkingsTile w={w} rate={cis.rate} vatTreatment={cis.vat_treatment} />
        </>
      )}
    </div>
  );
}
